/**
 * Holding cage: inspect/recount and IQC after Receive seals a receive container.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Loader2 } from "lucide-react";

import { parseInboundStationScan } from "@integrator-src/api/shipping-inbound/shipping-inbound-station-scan";
import { isPcbSku } from "@integrator-src/api/shipping-inbound/pcb-panel-iqc";

import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { LaborStationOperatorPresence } from "@/components/labor/LaborStationOperatorPresence";
import { useLaborReadOnlyStayActive } from "@/components/LaborReadOnlyStayNotice";
import { ApiError } from "@/lib/api";
import { useIntegratorMe } from "@/lib/me-query";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";

import { InboundIqcSessionModal, iqcQuantityFieldsFromLine } from "./InboundIqcSessionModal";
import { InboundHoldingSkuModal } from "./InboundReceiveExceptionModals";
import { InboundReceiveMpnEntryModal } from "./InboundReceiveMpnEntryModal";
import { InboundZplPrinterHeaderButton } from "./InboundZplPrinterHeaderButton";
import { useInboundZplPrinter } from "./inboundZplPrinter";
import {
  formatInboundSubcontainerHeading,
  parseInboundSubcontainerUnitKind,
} from "./inboundArrivalUnitKind";
import {
  canReleaseHoldingLines,
  groupHoldingTasksByInventoryCarton,
  holdingBlockersForLines,
  holdingReleaseTaskIds,
  isPurchaseOrderServiceSku,
  type HoldingBlocker,
} from "./inboundHoldingQueue";
import { InboundStationLaborTracker } from "./InboundStationLaborTracker";
import { receivingLaborWirePage } from "./inboundLaborPage";
import {
  fetchHoldingContainerHistory,
  fetchHoldingIqcLines,
  fetchHoldingTasks,
  formatNumber,
  printPcbPanelContainerLabels,
  HOLDING_REASON_LABELS,
  certifyHoldingMismatch,
  recordHoldingInspect,
  releaseHoldingTaskToStock,
  saveHoldingIqc,
  updateHoldingContainerQuantity,
  type HoldingIqcLine,
  type HoldingRecountEntry,
  type InboundHoldingContainer,
  type InboundHoldingReason,
  type InboundHoldingTask,
} from "./inboundApi";

const HOLDING_HOVER_HINT =
  "Cage work after Receive. A different person than the first count records inspection and IQC. "
  + "Each arrive-container is one row. Every hold on that container is shown on the same row. "
  + "Scan a carton Data Matrix, or type a SKU, GPO, PO, transfer, or reason, in the same field to narrow the queue. "
  + "Reasons can include IQC, damage, wrong SKU, packing-slip mismatch, invoice mismatch, or waiting on a vendor invoice. "
  + "Waiting on a vendor invoice keeps the receive count and does not ask for a recount. "
  + "A VALUE-ADD finished good stays here while it is waiting on finished goods, and that wait does not add on-hand. "
  + "Released cartons go to To stock with a clear inventory tag.";

const REASON_TONE: Record<InboundHoldingReason, BadgeTone> = {
  iqc: "neutral",
  damaged: "danger",
  sku_mismatch: "warning",
  packing_slip_mismatch: "warning",
  invoice_receipt_mismatch: "danger",
  waiting_on_invoice: "warning",
  unknown_mpn: "warning",
  awaiting_wofg: "warning",
  purchase_order_short: "danger",
  manufacturer_part_mismatch: "danger",
};

type HoldingQueueStatusFilter = "all" | "open" | "certified_mismatch" | "ready" | "waiting_on_invoice";
type HoldingQueueReasonFilter = "all" | InboundHoldingReason;
type HoldingQueueSortKey = "status" | "created_desc" | "created_asc" | "sku_asc" | "source_asc" | "reason_asc";

const HOLDING_QUEUE_SORT_OPTIONS: Array<{ value: HoldingQueueSortKey; label: string }> = [
  { value: "status", label: "Status" },
  { value: "source_asc", label: "GPO A to Z" },
  { value: "created_desc", label: "Newest first" },
  { value: "created_asc", label: "Oldest first" },
  { value: "sku_asc", label: "SKU A to Z" },
  { value: "reason_asc", label: "Reason A to Z" },
];

const HOLDING_QUEUE_STATUS_CHIP_OPTIONS: Array<{
  value: Exclude<HoldingQueueStatusFilter, "all">;
  label: string;
}> = [
  { value: "ready", label: "Ready to release" },
  { value: "open", label: "Open" },
  { value: "certified_mismatch", label: "Certified mismatch" },
  { value: "waiting_on_invoice", label: "Waiting on invoice" },
];

function isInvoiceReleaseReady(task: InboundHoldingTask, all: readonly InboundHoldingTask[]): boolean {
  return canReleaseHoldingLines([task], all);
}

function holdingTaskReasonLabel(task: InboundHoldingTask, all: readonly InboundHoldingTask[]): string {
  if (isInvoiceReleaseReady(task, all)) {
    return task.reason === "waiting_on_invoice" ? "Vendor invoice received" : "Invoice matched";
  }
  return HOLDING_REASON_LABELS[task.reason];
}

function holdingBlockerLabel(blocker: HoldingBlocker): string {
  if (blocker.kind === "megaventory_purchase_invoice") return "Megaventory purchase invoice";
  return HOLDING_REASON_LABELS[blocker.reason];
}

function holdingBlockerTone(blocker: HoldingBlocker): BadgeTone {
  if (blocker.kind === "megaventory_purchase_invoice") return "warning";
  return REASON_TONE[blocker.reason];
}

function holdingTaskReasonTone(task: InboundHoldingTask, all: readonly InboundHoldingTask[]): BadgeTone {
  if (isInvoiceReleaseReady(task, all)) {
    return "success";
  }
  return REASON_TONE[task.reason];
}

function holdingTaskSearchHaystack(task: InboundHoldingTask): string {
  const containerLabels = (task.containers ?? []).map((container) => holdingContainerLabel(container));
  return [
    task.sku,
    task.observed_sku,
    task.source_number,
    task.source_id,
    task.notes,
    HOLDING_REASON_LABELS[task.reason],
    ...containerLabels,
  ]
    .filter((value): value is string => value != null && value !== "")
    .join(" ")
    .toLowerCase();
}

function isWaitingOnInvoice(task: InboundHoldingTask, all: readonly InboundHoldingTask[]): boolean {
  if (isInvoiceReleaseReady(task, all)) return false;
  if (holdingBlockersForLines([task], all).some((blocker) => blocker.kind === "megaventory_purchase_invoice")) {
    return true;
  }
  return task.reason === "waiting_on_invoice";
}

/** Ready to release, then open work, then lines that cannot move until an invoice arrives. */
function holdingQueueStatusRank(task: InboundHoldingTask, all: readonly InboundHoldingTask[]): number {
  if (isInvoiceReleaseReady(task, all)) return 0;
  if (task.status === "certified_mismatch" || task.status === "mismatch") return 2;
  if (isWaitingOnInvoice(task, all)) return 3;
  if (task.status === "open") return 1;
  return 4;
}

function taskMatchesStatusFilter(
  task: InboundHoldingTask,
  statusFilter: HoldingQueueStatusFilter,
  all: readonly InboundHoldingTask[],
): boolean {
  if (statusFilter === "all") return true;
  if (statusFilter === "ready") return isInvoiceReleaseReady(task, all);
  if (statusFilter === "waiting_on_invoice") return isWaitingOnInvoice(task, all);
  if (statusFilter === "open") return task.status === "open" && !isWaitingOnInvoice(task, all);
  if (statusFilter === "certified_mismatch") {
    return task.status === "certified_mismatch" || task.status === "mismatch";
  }
  return false;
}

function compareHoldingTasks(
  left: InboundHoldingTask,
  right: InboundHoldingTask,
  sortKey: HoldingQueueSortKey,
  all: readonly InboundHoldingTask[],
): number {
  if (sortKey === "status") {
    const rank = holdingQueueStatusRank(left, all) - holdingQueueStatusRank(right, all);
    if (rank !== 0) return rank;
    const source = (left.source_number ?? left.source_id).localeCompare(right.source_number ?? right.source_id, undefined, {
      sensitivity: "base",
      numeric: true,
    });
    if (source !== 0) return source;
    return left.created_at.localeCompare(right.created_at);
  }
  switch (sortKey) {
    case "created_asc":
      return left.created_at.localeCompare(right.created_at);
    case "sku_asc":
      return (left.sku ?? left.observed_sku ?? "").localeCompare(right.sku ?? right.observed_sku ?? "", undefined, {
        sensitivity: "base",
      });
    case "source_asc":
      return (left.source_number ?? left.source_id).localeCompare(right.source_number ?? right.source_id, undefined, {
        sensitivity: "base",
        numeric: true,
      });
    case "reason_asc":
      return HOLDING_REASON_LABELS[left.reason].localeCompare(HOLDING_REASON_LABELS[right.reason], undefined, {
        sensitivity: "base",
      });
    case "created_desc":
    default:
      return right.created_at.localeCompare(left.created_at);
  }
}

function filterAndSortHoldingTasks(
  tasks: InboundHoldingTask[],
  search: string,
  statusFilter: HoldingQueueStatusFilter,
  reasonFilter: HoldingQueueReasonFilter,
  sortKey: HoldingQueueSortKey,
): InboundHoldingTask[] {
  const needle = search.trim().toLowerCase();
  const filtered = tasks.filter((task) => {
    if (!taskMatchesStatusFilter(task, statusFilter, tasks)) {
      return false;
    }
    if (reasonFilter !== "all" && task.reason !== reasonFilter) {
      return false;
    }
    if (needle !== "" && !holdingTaskSearchHaystack(task).includes(needle)) {
      return false;
    }
    return true;
  });
  return [...filtered].sort((left, right) => compareHoldingTasks(left, right, sortKey, tasks));
}

/** Arrival-package tags and inventory-box tags load that receipt's holding lines. Other text filters the queue. */
function holdingCartonScan(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") return "";
  const parsed = parseInboundStationScan(trimmed);
  if (parsed?.kind === "package_token" || parsed?.kind === "inventory_box") return trimmed;
  return "";
}

function holdingCorrectionCode(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = error.body;
  if (body == null || typeof body !== "object" || !("code" in body)) return null;
  const code = (body as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}

function formatHoldingDate(value: string | null | undefined): string | null {
  if (value == null || value.trim() === "") return null;
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/Chicago",
  });
}

function holdingLineLabelCode(line: InboundHoldingTask): string {
  for (const container of line.containers ?? []) {
    const code = container.label_code?.trim() ?? "";
    if (code !== "") return code;
  }
  return "";
}

function holdingContainerLabel(container: InboundHoldingContainer): string {
  const labelCode = container.label_code?.trim() ?? "";
  if (labelCode !== "") return labelCode;
  const kind = container.unit_kind?.trim() ?? "";
  if (kind === "") return `Container ${String(container.subcontainer_no)}`;
  return formatInboundSubcontainerHeading(parseInboundSubcontainerUnitKind(kind), container.subcontainer_no);
}

function parseOptionalIqcQuantity(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function InboundHoldingPage(): JSX.Element {
  usePageBreadcrumb("Holding");
  const queryClient = useQueryClient();
  const me = useIntegratorMe();
  const isShippingAdmin = useHubModuleAdmin("shipping");
  const myId = me.data?.user.id ?? null;
  const laborReadOnly = useLaborReadOnlyStayActive();
  const { station, setStation, selectedPrinter, unavailableMessage, documents } = useInboundZplPrinter();

  const [searchParams] = useSearchParams();
  const [findQuery, setFindQuery] = useState(() => searchParams.get("scan")?.trim() ?? "");
  const findRef = useRef<HTMLInputElement | null>(null);
  const cartonScan = holdingCartonScan(findQuery);
  const textSearch = cartonScan === "" ? findQuery : "";
  const holdingKey = ["shipping", "inbound", "holding-tasks", cartonScan] as const;
  const iqcKey = ["shipping", "inbound", "holding-iqc", cartonScan] as const;
  const tasksQuery = useQuery({
    queryKey: holdingKey,
    queryFn: () => fetchHoldingTasks({ status: "open,mismatch,certified_mismatch", scan: cartonScan }),
  });
  const iqcQuery = useQuery({
    queryKey: iqcKey,
    queryFn: () => fetchHoldingIqcLines(cartonScan),
  });

  useFloorPageScanCapture({
    inputRef: findRef,
    onScan: (value) => setFindQuery(value),
  });

  const [countByTask, setCountByTask] = useState<Record<string, string>>({});
  const [iqcLineId, setIqcLineId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skuModal, setSkuModal] = useState<{
    taskId: string;
    containerId: string;
    quantity: number;
    currentSku: string;
  } | null>(null);
  const [mpnModal, setMpnModal] = useState<{
    taskId: string;
    containerId: string;
    quantity: number;
    sku: string;
  } | null>(null);
  const [queueStatusFilter, setQueueStatusFilter] = useState<HoldingQueueStatusFilter>("all");
  const [queueReasonFilter, setQueueReasonFilter] = useState<HoldingQueueReasonFilter>("all");
  const [queueSortKey, setQueueSortKey] = useState<HoldingQueueSortKey>("status");

  const containerMutation = useMutation({
    mutationFn: async (input: {
      taskId: string;
      containerId: string;
      quantity: number;
      sku?: string;
      scannedMpn?: string;
      manufacturerName?: string;
    }) => {
      if (laborReadOnly) return;
      await updateHoldingContainerQuantity(
        input.taskId,
        input.containerId,
        input.quantity,
        selectedPrinter.trim(),
        { sku: input.sku, scannedMpn: input.scannedMpn, manufacturerName: input.manufacturerName },
      );
    },
    onSuccess: () => {
      setError(null);
      setSkuModal(null);
      setMpnModal(null);
      void queryClient.invalidateQueries({ queryKey: holdingKey });
    },
    onError: (mutationError, variables) => {
      const code = holdingCorrectionCode(mutationError);
      if ((code === "mpn_required" || code === "mpn_unmatched") && variables.sku != null && variables.sku.trim() !== "") {
        setSkuModal(null);
        setMpnModal({
          taskId: variables.taskId,
          containerId: variables.containerId,
          quantity: variables.quantity,
          sku: variables.sku,
        });
        setError(code === "mpn_unmatched" && mutationError instanceof Error ? mutationError.message : null);
        return;
      }
      setError(mutationError instanceof ApiError ? mutationError.message : String(mutationError));
    },
  });
  const certifyMutation = useMutation({
    mutationFn: async (taskId: string) => {
      if (laborReadOnly) return;
      await certifyHoldingMismatch(taskId);
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: holdingKey });
    },
    onError: (mutationError) =>
      setError(mutationError instanceof ApiError ? mutationError.message : String(mutationError)),
  });
  useEffect(() => {
    if (laborReadOnly) setIqcLineId(null);
  }, [laborReadOnly]);

  const inspectMutation = useMutation({
    mutationFn: async (input: { taskId: string; quantity: number }) => {
      if (laborReadOnly) return;
      await recordHoldingInspect(input.taskId, input.quantity);
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: holdingKey });
      void queryClient.invalidateQueries({ queryKey: iqcKey });
    },
    onError: (mutationError) =>
      setError(mutationError instanceof ApiError ? mutationError.message : String(mutationError)),
  });

  const releaseMutation = useMutation({
    mutationFn: async (taskIds: string[]) => {
      if (laborReadOnly) return;
      for (const taskId of taskIds) {
        await releaseHoldingTaskToStock(taskId);
      }
    },
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: holdingKey });
      void queryClient.invalidateQueries({ queryKey: ["shipping", "inbound", "to-stock"] });
    },
    onError: (mutationError) =>
      setError(mutationError instanceof ApiError ? mutationError.message : String(mutationError)),
  });

  const pcbIqcSavedIdRef = useRef<string | null>(null);
  const [pcbIqcSavedId, setPcbIqcSavedId] = useState<string | null>(null);
  const iqcMutation = useMutation({
    mutationFn: async (input: {
      itemId: string;
      iqc_status: HoldingIqcLine["iqc_status"];
      iqc_notes: string;
      iqc_passed_quantity: number | null;
      iqc_failed_quantity: number | null;
      iqc_skipped_quantity: number | null;
      pcb_full_panel_quantity: number | null;
      pcb_partial_panel_quantity: number | null;
      pcb_partial_panel_count: number | null;
      printPcbLabels: boolean;
    }) => {
      if (laborReadOnly) return;
      if (!input.printPcbLabels || pcbIqcSavedIdRef.current !== input.itemId) {
        await saveHoldingIqc(input.itemId, input);
        if (input.printPcbLabels) {
          pcbIqcSavedIdRef.current = input.itemId;
          setPcbIqcSavedId(input.itemId);
        }
      }
      if (input.printPcbLabels) {
        const printer = selectedPrinter.trim();
        if (printer === "") {
          throw new Error("Select a 4x2 label printer in the header.");
        }
        await printPcbPanelContainerLabels(input.itemId, printer);
      }
    },
    onSuccess: () => {
      setError(null);
      pcbIqcSavedIdRef.current = null;
      setPcbIqcSavedId(null);
      setIqcLineId(null);
      void queryClient.invalidateQueries({ queryKey: iqcKey });
    },
    onError: (mutationError) =>
      setError(mutationError instanceof ApiError ? mutationError.message : String(mutationError)),
  });

  const tasks = tasksQuery.data ?? [];
  const filteredTasks = useMemo(
    () => filterAndSortHoldingTasks(tasks, textSearch, queueStatusFilter, queueReasonFilter, queueSortKey),
    [tasks, textSearch, queueReasonFilter, queueSortKey, queueStatusFilter],
  );
  const queueCards = useMemo(
    () => groupHoldingTasksByInventoryCarton(filteredTasks, tasks),
    [filteredTasks, tasks],
  );
  const queueCardTotal = useMemo(() => groupHoldingTasksByInventoryCarton(tasks, tasks).length, [tasks]);
  const queueFiltersActive =
    findQuery.trim() !== "" || queueStatusFilter !== "all" || queueReasonFilter !== "all";
  const iqcLines = iqcQuery.data ?? [];
  const iqcLine = iqcLines.find((row) => row.id === iqcLineId) ?? null;

  return (
    <div className="space-y-4">
      <InboundStationLaborTracker wirePage={receivingLaborWirePage("holding")} workLabel="Holding" />
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={HOLDING_HOVER_HINT}>Holding</PageTitleWithHelp>
        <div className="flex shrink-0 flex-wrap items-end gap-2">
          <LaborStationOperatorPresence stationPage="receiving" receivingFloor="holding" />
          <InboundZplPrinterHeaderButton
            station={station}
            onStationChange={setStation}
            unavailableMessage={unavailableMessage}
          
      documents={documents}
    />
        </div>
      </header>

      {error ? (
        <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </p>
      ) : null}

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-900">Inspection queue</h2>
          {!tasksQuery.isLoading && !tasksQuery.isError && tasks.length > 0 ? (
            <span className="text-sm text-slate-500">
              {queueCards.length === queueCardTotal
                ? `${queueCards.length} container${queueCards.length === 1 ? "" : "s"}`
                : `Showing ${queueCards.length} of ${queueCardTotal}`}
            </span>
          ) : null}
        </div>

        {!tasksQuery.isLoading && !tasksQuery.isError && (tasks.length > 0 || findQuery.trim() !== "") ? (
          <Card data-labor-readonly-allow>
            <CardHeader>
              <CardTitle>Filters</CardTitle>
            </CardHeader>
            <CardBody className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-1 md:col-span-2 xl:col-span-4">
                <Label htmlFor="holding-find">Scan or search holding inspections</Label>
                <Input
                  id="holding-find"
                  ref={findRef}
                  type="search"
                  value={findQuery}
                  onChange={(event) => setFindQuery(event.target.value)}
                  placeholder="Scan the carton Data Matrix, or filter by SKU, GPO, PO, transfer, or reason"
                  autoComplete="off"
                  {...FLOOR_PAGE_SCAN_TARGET_PROPS}
                />
              </div>
              <div className="grid gap-3 md:col-span-2 md:grid-cols-[minmax(11rem,18.75rem)_minmax(11rem,18.75rem)_minmax(0,1fr)] xl:col-span-4">
                <div className="space-y-1">
                  <Label htmlFor="holding-queue-reason">Reason</Label>
                  <select
                    id="holding-queue-reason"
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                    value={queueReasonFilter}
                    onChange={(event) => setQueueReasonFilter(event.target.value as HoldingQueueReasonFilter)}
                  >
                    <option value="all">All reasons</option>
                    {(Object.keys(HOLDING_REASON_LABELS) as InboundHoldingReason[]).map((reason) => (
                      <option key={reason} value={reason}>
                        {HOLDING_REASON_LABELS[reason]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="holding-queue-sort">Sort</Label>
                  <select
                    id="holding-queue-sort"
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                    value={queueSortKey}
                    onChange={(event) => setQueueSortKey(event.target.value as HoldingQueueSortKey)}
                  >
                    {HOLDING_QUEUE_SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <div className="flex h-10 flex-wrap items-center gap-2">
                    {HOLDING_QUEUE_STATUS_CHIP_OPTIONS.map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        size="sm"
                        variant={queueStatusFilter === option.value ? "primary" : "secondary"}
                        aria-pressed={queueStatusFilter === option.value}
                        onClick={() =>
                          setQueueStatusFilter((current) => (current === option.value ? "all" : option.value))
                        }
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              {queueFiltersActive ? (
                <div className="md:col-span-2 xl:col-span-4">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setFindQuery("");
                      setQueueStatusFilter("all");
                      setQueueReasonFilter("all");
                    }}
                  >
                    Clear filters
                  </Button>
                </div>
              ) : null}
            </CardBody>
          </Card>
        ) : null}

        {tasksQuery.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading holding tasks.
          </p>
        ) : tasksQuery.isError ? (
          <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle className="h-4 w-4" />
            Could not load holding tasks.{" "}
            <button type="button" data-labor-readonly-allow className="underline" onClick={() => void tasksQuery.refetch()}>
              Retry
            </button>
          </p>
        ) : queueCards.length === 0 ? (
          <Card>
            <CardBody>
              <p className="text-sm text-slate-500">
                {queueFiltersActive
                  ? "No holding inspections match these filters."
                  : "No open holding inspections."}
              </p>
            </CardBody>
          </Card>
        ) : (
          <ul className="space-y-3">
            {queueCards.map((card) => (
              <HoldingTaskCard
                key={card.key}
                task={card.task}
                lines={card.lines}
                focusContainer={card.focusContainer}
                queueTasks={tasks}
                holdNotes={card.notes}
                myId={myId}
                isShippingAdmin={isShippingAdmin}
                value={countByTask[card.task.id] ?? ""}
                inspectPending={inspectMutation.isPending}
                certifyPending={certifyMutation.isPending}
                releasePending={releaseMutation.isPending}
                printerSelected={selectedPrinter.trim() !== ""}
                containerPendingId={
                  containerMutation.isPending ? (containerMutation.variables?.containerId ?? null) : null
                }
                onChange={(value) => setCountByTask((prev) => ({ ...prev, [card.task.id]: value }))}
                onInspect={(quantity) => inspectMutation.mutate({ taskId: card.task.id, quantity })}
                onCertify={() => certifyMutation.mutate(card.task.id)}
                onUpdateContainer={(containerId, quantity) =>
                  containerMutation.mutate({ taskId: card.task.id, containerId, quantity })
                }
                onChangeSku={(containerId, quantity) =>
                  setSkuModal({
                    taskId: card.task.id,
                    containerId,
                    quantity,
                    currentSku: card.task.sku ?? card.task.observed_sku ?? "",
                  })
                }
                onRelease={() => {
                  const readyIds = holdingReleaseTaskIds(card.lines, tasks);
                  releaseMutation.mutate(readyIds.length > 0 ? readyIds : [card.task.id]);
                }}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-slate-900">IQC</h2>
        {iqcQuery.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading IQC lines.
          </p>
        ) : iqcQuery.isError ? (
          <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle className="h-4 w-4" />
            Could not load IQC.{" "}
            <button type="button" data-labor-readonly-allow className="underline" onClick={() => void iqcQuery.refetch()}>
              Retry
            </button>
          </p>
        ) : iqcLines.length === 0 ? (
          <Card>
            <CardBody>
              <p className="text-sm text-slate-500">No HOLD lines waiting for IQC.</p>
            </CardBody>
          </Card>
        ) : (
          <ul className="space-y-3">
            {iqcLines.map((line) => {
              const source = line.source_number ?? `${line.source_type} ${line.source_id}`;
              const sameUser = myId != null && line.first_count_by === myId;
              return (
                <Card key={line.id}>
                  <CardHeader className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <span className="font-mono">{line.sku}</span>
                      <span className="text-sm font-normal text-slate-500">{source}</span>
                    </CardTitle>
                    <Badge tone="warning">IQC pending</Badge>
                  </CardHeader>
                  <CardBody className="flex flex-wrap items-center gap-3">
                    <span className="text-sm text-slate-600">
                      Counted{" "}
                      <span className="font-semibold text-slate-900">{formatNumber(line.received_quantity)}</span>
                    </span>
                    <Button size="sm" disabled={sameUser} onClick={() => setIqcLineId(line.id)}>
                      Start IQC
                    </Button>
                    {sameUser ? (
                      <span className="text-xs text-slate-500">
                        You entered the Receive count. A different person must do IQC.
                      </span>
                    ) : null}
                  </CardBody>
                </Card>
              );
            })}
          </ul>
        )}
      </section>

      <InboundIqcSessionModal
        open={iqcLine != null}
        sku={iqcLine?.sku ?? ""}
        description={null}
        countedQuantity={Number(iqcLine?.received_quantity ?? 0)}
        initialNotes={iqcLine?.iqc_notes ?? ""}
        initialQuantities={iqcQuantityFieldsFromLine({
          passed: iqcLine?.iqc_passed_quantity,
          failed: iqcLine?.iqc_failed_quantity,
          skipped: iqcLine?.iqc_skipped_quantity,
        })}
        saving={iqcMutation.isPending}
        printerReady={selectedPrinter.trim() !== ""}
        labelsSaved={iqcLine != null && pcbIqcSavedId === iqcLine.id}
        onCancel={() => {
          if (pcbIqcSavedIdRef.current != null) {
            setError("PCB IQC was saved, but the container labels were not printed.");
          }
          pcbIqcSavedIdRef.current = null;
          setPcbIqcSavedId(null);
          setIqcLineId(null);
        }}
        onSave={(payload) => {
          if (iqcLine == null) return;
          const printPcbLabels = isPcbSku(iqcLine.sku);
          iqcMutation.mutate({
            itemId: iqcLine.id,
            iqc_status: payload.iqc_status,
            iqc_notes: payload.iqc_notes,
            iqc_passed_quantity: parseOptionalIqcQuantity(payload.iqc_passed_quantity),
            iqc_failed_quantity: parseOptionalIqcQuantity(payload.iqc_failed_quantity),
            iqc_skipped_quantity: parseOptionalIqcQuantity(payload.iqc_skipped_quantity),
            pcb_full_panel_quantity: parseOptionalIqcQuantity(payload.pcb_full_panel_quantity),
            pcb_partial_panel_quantity: parseOptionalIqcQuantity(payload.pcb_partial_panel_quantity),
            pcb_partial_panel_count: parseOptionalIqcQuantity(payload.pcb_partial_panel_count),
            printPcbLabels,
          });
        }}
      />
      <InboundHoldingSkuModal
        open={skuModal != null}
        currentSku={skuModal?.currentSku ?? ""}
        pending={containerMutation.isPending}
        onClose={() => setSkuModal(null)}
        onSubmit={(sku) => {
          if (skuModal == null) return;
          containerMutation.mutate({
            taskId: skuModal.taskId,
            containerId: skuModal.containerId,
            quantity: skuModal.quantity,
            sku,
          });
        }}
      />
      <InboundReceiveMpnEntryModal
        open={mpnModal != null}
        sku={mpnModal?.sku ?? ""}
        verifying={containerMutation.isPending}
        onClose={() => setMpnModal(null)}
        onVerify={(mpn, manufacturerName) => {
          if (mpnModal == null) return;
          containerMutation.mutate({
            taskId: mpnModal.taskId,
            containerId: mpnModal.containerId,
            quantity: mpnModal.quantity,
            sku: mpnModal.sku,
            scannedMpn: mpn,
            manufacturerName: manufacturerName ?? undefined,
          });
        }}
      />
    </div>
  );
}

function formatRecountWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso === "" ? "" : iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function recountEntryText(entry: HoldingRecountEntry): string {
  if (entry.kind === "first_count") return `First count ${formatNumber(entry.quantity_after)}`;
  const before = entry.quantity_before == null ? "" : `${formatNumber(entry.quantity_before)} to `;
  return `Recount ${before}${formatNumber(entry.quantity_after)}`;
}

function HoldingContainerHistoryModal({
  taskId,
  containerId,
  label,
  onClose,
}: {
  taskId: string;
  containerId: string;
  label: string;
  onClose: () => void;
}): JSX.Element {
  const historyQuery = useQuery({
    queryKey: ["holding-container-history", taskId, containerId],
    queryFn: () => fetchHoldingContainerHistory(taskId, containerId),
  });
  const entries = historyQuery.data?.entries ?? [];
  const titleLabel = historyQuery.data?.label_code?.trim() || label;

  return createPortal(
    <div {...hubModalOverlayProps(true)} onClick={onClose}>
      <Card className="mt-10 w-full max-w-lg shadow-xl" onClick={(event) => event.stopPropagation()}>
        <CardHeader className="flex items-center justify-between gap-2 border-b border-slate-200">
          <CardTitle className="text-base">
            History <span className="font-mono">{titleLabel}</span>
          </CardTitle>
          <Button type="button" size="sm" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </CardHeader>
        <CardBody>
          {historyQuery.isLoading ? (
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading history.
            </p>
          ) : historyQuery.isError ? (
            <p className="text-sm text-red-600">Could not load recount history.</p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-slate-500">No recounts recorded for this container yet.</p>
          ) : (
            <ol className="space-y-3">
              {entries.map((entry, index) => (
                <li key={entry.id} className="text-sm">
                  <p className="font-medium text-slate-900">
                    {index + 1}. {recountEntryText(entry)}
                  </p>
                  <p className="text-slate-600">
                    {entry.actor_display_name?.trim() || "Unknown"}
                    {entry.at.trim() !== "" ? ` · ${formatRecountWhen(entry.at)}` : ""}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

function HoldingTaskCard({
  task,
  lines,
  focusContainer,
  queueTasks,
  holdNotes,
  myId,
  isShippingAdmin,
  value,
  inspectPending,
  certifyPending,
  releasePending,
  printerSelected,
  containerPendingId,
  onChange,
  onInspect,
  onCertify,
  onUpdateContainer,
  onChangeSku,
  onRelease,
}: {
  task: InboundHoldingTask;
  lines: InboundHoldingTask[];
  focusContainer: InboundHoldingContainer | null;
  queueTasks: InboundHoldingTask[];
  holdNotes: string[];
  myId: string | null;
  isShippingAdmin: boolean;
  value: string;
  inspectPending: boolean;
  certifyPending: boolean;
  releasePending: boolean;
  printerSelected: boolean;
  containerPendingId: string | null;
  onChange: (value: string) => void;
  onInspect: (quantity: number) => void;
  onCertify: () => void;
  onUpdateContainer: (containerId: string, quantity: number) => void;
  onChangeSku: (containerId: string, quantity: number) => void;
  onRelease: () => void;
}): JSX.Element {
  const containers = focusContainer != null ? [focusContainer] : (task.containers ?? []);
  const containerKey = containers.map((container) => `${container.id}:${container.quantity}`).join("|");
  const [containerQuantities, setContainerQuantities] = useState<Record<string, string>>(() =>
    Object.fromEntries(containers.map((container) => [container.id, container.quantity])),
  );
  const [historyContainerId, setHistoryContainerId] = useState<string | null>(null);
  useEffect(() => {
    setContainerQuantities(
      Object.fromEntries(containers.map((container) => [container.id, container.quantity])),
    );
  }, [containerKey]);
  const sameUser = myId != null && task.first_count_by === myId;
  const qtyValid = /^\d+(\.\d+)?$/.test(value.trim());
  const blockers = holdingBlockersForLines(lines, queueTasks);
  const releaseReady = canReleaseHoldingLines(lines, queueTasks);
  const actionableBlocker = blockers.some(
    (blocker) =>
      blocker.kind === "reason"
      && blocker.reason !== "waiting_on_invoice"
      && blocker.reason !== "awaiting_wofg",
  );
  const waitingOnInvoice = !releaseReady && !actionableBlocker && blockers.length > 0;
  const awaitingWofg =
    task.reason === "awaiting_wofg" && (task.status === "open" || task.status === "mismatch");
  const recountOpen =
    (task.status === "open" || task.status === "mismatch") && !releaseReady && !waitingOnInvoice && !awaitingWofg;
  const containerCorrectionOpen =
    containers.length > 0
    && !releaseReady
    && !waitingOnInvoice
    && !awaitingWofg
    && (task.status === "open" || task.status === "mismatch" || task.status === "certified_mismatch");
  const inspectionDisagrees =
    task.inspect_quantity != null
    && Number(task.inspect_quantity) !== Number(task.first_count_quantity);
  const quantityDisagrees = Number(task.quantity) !== Number(task.first_count_quantity);
  const canCertify = isShippingAdmin && recountOpen && (inspectionDisagrees || quantityDisagrees);
  const source =
    task.source_number ?? `${task.source_type === "purchase_order" ? "PO" : "Transfer"} ${task.source_id}`;
  const lineCodes = lines.map(holdingLineLabelCode);
  const sharedLabel =
    lineCodes.length > 0 && lineCodes.every((code) => code !== "" && code === lineCodes[0]) ? lineCodes[0] : null;
  const receiveLabel =
    sharedLabel == null && focusContainer == null && task.receive_subcontainer_no != null
      ? holdingContainerLabel({
          id: task.receive_container_id ?? "",
          subcontainer_no: task.receive_subcontainer_no,
          unit_kind: task.receive_unit_kind ?? null,
          quantity: task.quantity,
        })
      : null;
  const showLineList = lines.length > 1 || lines.some((line) => isPurchaseOrderServiceSku(line.sku));
  const arrivedOn = formatHoldingDate(task.arrived_at);
  const receivedOn = formatHoldingDate(task.received_at);

  const historyContainer = containers.find((container) => container.id === historyContainerId) ?? null;

  return (
    <>
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          {sharedLabel != null ? (
            <span className="font-mono">{sharedLabel}</span>
          ) : focusContainer != null && lineCodes.every((code) => code === "") ? (
            <span className="font-mono">{holdingContainerLabel(focusContainer)}</span>
          ) : receiveLabel != null ? (
            <span className="font-mono">{receiveLabel}</span>
          ) : null}
          {showLineList ? null : (
            <span className="font-mono">{task.sku ?? task.observed_sku ?? "—"}</span>
          )}
          <span className="text-sm font-normal text-slate-500">{source}</span>
          {arrivedOn != null ? (
            <span className="text-sm font-normal text-slate-500">Arrived {arrivedOn}</span>
          ) : null}
          {receivedOn != null ? (
            <span className="text-sm font-normal text-slate-500">Received {receivedOn}</span>
          ) : null}
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {releaseReady ? (
            <Badge tone={holdingTaskReasonTone(task, queueTasks)}>{holdingTaskReasonLabel(task, queueTasks)}</Badge>
          ) : (
            blockers.map((blocker) => (
              <Badge key={holdingBlockerLabel(blocker)} tone={holdingBlockerTone(blocker)}>
                {holdingBlockerLabel(blocker)}
              </Badge>
            ))
          )}
          {releaseReady ? (
            <Badge tone="success">Ready to release</Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {showLineList ? (
          <ul className="space-y-1 text-sm text-slate-700">
            {lines.map((line, index) => {
              const description = isPurchaseOrderServiceSku(line.sku) ? line.line_description?.trim() ?? "" : "";
              const labelCode = sharedLabel == null ? lineCodes[index] ?? "" : "";
              return (
                <li key={line.id} className="flex flex-wrap items-baseline gap-x-2">
                  {labelCode !== "" ? (
                    <span className="font-mono font-semibold text-slate-900">{labelCode}</span>
                  ) : null}
                  <span className="font-mono font-semibold text-slate-900">{line.sku ?? "—"}</span>
                  {description !== "" ? <span>{description}</span> : null}
                  <span className="tabular-nums text-slate-500">{formatNumber(line.quantity)}</span>
                </li>
              );
            })}
          </ul>
        ) : null}
        {task.observed_sku != null && task.observed_sku !== task.sku ? (
          <p className="text-sm text-slate-600">
            Observed SKU <span className="font-mono font-semibold text-slate-900">{task.observed_sku}</span>
          </p>
        ) : null}
        {!releaseReady
          ? holdNotes.map((note) => (
              <p key={note} className="text-sm text-slate-600">
                {note}
              </p>
            ))
          : null}
        {task.reference_quantity != null ? (
          <p className="text-sm text-slate-600">
            Packing slip shows{" "}
            <span className="font-semibold text-slate-900">{formatNumber(task.reference_quantity)}</span>
            {" · "}
            sealed total{" "}
            <span className="font-semibold text-slate-900">{formatNumber(task.quantity)}</span>
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
          <span>
            First count{" "}
            <span className="font-semibold text-slate-900">{formatNumber(task.first_count_quantity)}</span>
          </span>
          {task.inspect_quantity != null ? (
            <span>
              Inspection{" "}
              <span className="font-semibold text-slate-900">{formatNumber(task.inspect_quantity)}</span>
            </span>
          ) : null}
        </div>

        {waitingOnInvoice ? (
          <div className="space-y-2">
            {blockers.some((blocker) => blocker.kind === "megaventory_purchase_invoice") ? (
              <p className="text-sm text-slate-700">
                This line stays on Holding until the purchase invoice is in Megaventory.
              </p>
            ) : null}
            {containers.map((container) => {
              const label = holdingContainerLabel(container);
              return (
                <div key={container.id} className="flex flex-wrap items-center gap-2">
                  <span className="min-w-28 font-mono text-sm font-semibold text-slate-900">{label}</span>
                  <span className="text-sm text-slate-600">
                    First count{" "}
                    <span className="font-semibold text-slate-900">
                      {formatNumber(container.first_count_quantity ?? container.quantity)}
                    </span>
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setHistoryContainerId(container.id)}
                  >
                    History
                  </Button>
                </div>
              );
            })}
          </div>
        ) : containerCorrectionOpen ? (
          <div className="space-y-2">
            {containers.map((container) => {
              const draft = containerQuantities[container.id] ?? container.quantity;
              const quantityValid = /^[1-9]\d*$/.test(draft.trim());
              const pending = containerPendingId === container.id;
              const label = holdingContainerLabel(container);
              return (
                <div key={container.id} className="flex flex-wrap items-center gap-2">
                  <span className="min-w-28 font-mono text-sm font-semibold text-slate-900">{label}</span>
                  <span className="text-sm text-slate-600">
                    First count{" "}
                    <span className="font-semibold text-slate-900">
                      {formatNumber(container.first_count_quantity ?? container.quantity)}
                    </span>
                  </span>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    className="h-9 w-32"
                    aria-label={`Quantity for ${label}`}
                    value={draft}
                    disabled={pending}
                    onChange={(event) =>
                      setContainerQuantities((current) => ({ ...current, [container.id]: event.target.value }))
                    }
                  />
                  <Button
                    size="sm"
                    className="gap-1"
                    disabled={!printerSelected || !quantityValid || pending || containerPendingId != null}
                    onClick={() => onUpdateContainer(container.id, Number(draft.trim()))}
                  >
                    {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    Update and print
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!printerSelected || !quantityValid || pending || containerPendingId != null}
                    onClick={() => onChangeSku(container.id, Number(draft.trim()))}
                  >
                    Change SKU
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setHistoryContainerId(container.id)}
                  >
                    History
                  </Button>
                </div>
              );
            })}
            {!printerSelected ? (
              <p className="text-xs text-slate-500">Select a 4x2 label printer in the header.</p>
            ) : null}
            {task.status === "certified_mismatch" ? (
              <p className="text-sm text-rose-800">
                A shipping administrator certified this count mismatch. Change the SKU or quantity, then reprint the label.
              </p>
            ) : null}
            {canCertify ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={certifyPending}
                onClick={onCertify}
              >
                Certify mismatch
              </Button>
            ) : null}
          </div>
        ) : awaitingWofg ? (
          // Stays on Holding until Release to To stock. See docs/inbound-holding-release.md.
          <div className="space-y-2">
            <p className="text-sm text-slate-700">
              This line stays on Holding until you release it to To stock, so someone can physically move the carton.
            </p>
            <Button size="sm" className="gap-1" disabled={releasePending} onClick={onRelease}>
              <Check className="h-3.5 w-3.5" />
              Release to To stock
            </Button>
          </div>
        ) : recountOpen ? (
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              className="h-9 w-32"
              placeholder="Inspect qty"
              aria-label={`Inspection count for ${task.sku ?? "line"}`}
              value={value}
              disabled={sameUser}
              onChange={(event) => onChange(event.target.value)}
            />
            <Button
              size="sm"
              className="gap-1"
              disabled={sameUser || !qtyValid || inspectPending}
              onClick={() => onInspect(Number(value.trim()))}
            >
              <Check className="h-3.5 w-3.5" />
              Record inspection
            </Button>
            {canCertify ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={certifyPending}
                onClick={onCertify}
              >
                Certify mismatch
              </Button>
            ) : null}
            {sameUser ? (
              <span className="text-xs text-slate-500">
                You entered the first count. A different person must inspect.
              </span>
            ) : null}
          </div>
        ) : releaseReady ? (
          <div className="space-y-2">
            <p className="text-sm text-emerald-800">
              {lines.length > 1
                ? "Vendor invoice matched this receipt. Release this container to To stock when it is ready to put away."
                : "Vendor invoice matched this receipt. Release the line to To stock when the carton is ready to put away."}
            </p>
            {containers.length > 0 ? (
              <div className="space-y-2">
                {containers.map((container) => {
                  const label = holdingContainerLabel(container);
                  return (
                    <div key={container.id} className="flex flex-wrap items-center gap-2">
                      <span className="min-w-28 font-mono text-sm font-semibold text-slate-900">{label}</span>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => setHistoryContainerId(container.id)}
                      >
                        History
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : null}
            <Button size="sm" className="gap-1" disabled={releasePending} onClick={onRelease}>
              <Check className="h-3.5 w-3.5" />
              Release to To stock
            </Button>
          </div>
        ) : task.status === "certified_mismatch" || task.status === "mismatch" ? (
          <p className="text-sm text-rose-800">
            A shipping administrator certified this count mismatch. People monitoring vendor invoices were notified.
          </p>
        ) : null}
      </CardBody>
    </Card>
    {historyContainer != null ? (
      <HoldingContainerHistoryModal
        taskId={task.id}
        containerId={historyContainer.id}
        label={holdingContainerLabel(historyContainer)}
        onClose={() => setHistoryContainerId(null)}
      />
    ) : null}
    </>
  );
}
